import * as React from 'react';
import Chip from '@mui/material/Chip';
import { HtmlTooltip } from '../../../../components/commonComponents/StyledComponents/styled';
import { Box, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { fetchBrandUsers } from '../../../../api_calls/userManagement';
import { useDispatch, useSelector } from 'react-redux';
import { updateStateData } from '../../../../Store/actions';
import { toast } from 'react-toastify';
import EmailRecipientSelector from './EmailRecipientSelector';
import { validateEmailFormat, validateEmailFormatWithReason } from '../../../../api_calls/utils';

const normalizeEmailValue = (email) =>
    String(email || "")
        .replace(/^New:\s*/i, "")
        .replace(/:\s*\(Already Shared\)$/i, "")
        .trim()
        .toLowerCase();

const EmailSelection = ({ fieldErrors }) => {
    const userStateDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();

    // Emails already shared with this report should not be selectable again.
    const sharedUserListitems = React.useMemo(() => (
        typeof userStateDetails.shared_report_users_list !== "undefined" && userStateDetails.shared_report_users_list.length > 0
            ? userStateDetails.shared_report_users_list.map(sharedUser => sharedUser.email)
            : []
    ), [userStateDetails.shared_report_users_list]);

    // Only non-shared brand users become dropdown options in the custom selector.
    const options = React.useMemo(() => (
        typeof userStateDetails.brand_user_list_options !== "undefined"
            ? userStateDetails.brand_user_list_options.map((option) => option.label).filter(optionLabel => !sharedUserListitems.includes(optionLabel))
            : []
    ), [sharedUserListitems, userStateDetails.brand_user_list_options]);

    const selectedEmails = typeof userStateDetails.selected_brand_userforReportshare !== "undefined"
        ? userStateDetails.selected_brand_userforReportshare
        : [];

    // Strip the "New:" marker for favorite-chip visibility checks only.
    const selectedEmailLabels = selectedEmails.map((email) =>
        email.includes("New: ") ? email.replace("New: ", "") : email
    );

    const emailListError =
        typeof fieldErrors !== "undefined" && typeof fieldErrors.email_list !== "undefined"
            ? fieldErrors.email_list
            : "";

    React.useEffect(() => {
        fetchBrandUsers();
    }, []);

    const handleEmailChange = (e, selectedOptions, action) => {
        // Block already-shared emails, matching the old Autocomplete guard.
        const hasAlreadySharedSelection = selectedOptions.some(
            (selectedOption) =>
                selectedOption.includes(": (Already Shared)") ||
                sharedUserListitems.some(
                    (email) => normalizeEmailValue(email) === normalizeEmailValue(selectedOption)
                )
        );
        if (hasAlreadySharedSelection) {
            toast("This e-mail is already shared for this report.", { type: "error" });
            return;
        }

        const normalizedSelectedEmails = selectedOptions
            .map(normalizeEmailValue)
            .filter((email) => email !== "");

        // validate email format 
        // const validFormat = normalizedSelectedEmails.some(
        //     (selectedEmail) => validateEmailFormat(selectedEmail) === false
        // );

        // if (validFormat) {
        //     toast("Please use a valid e-mail format like name@example.com", { type: "error" });
        //     return;
        // }

        const invalidEmail = normalizedSelectedEmails
            .map((email) => ({
                email,
                validation: validateEmailFormatWithReason(email),
            }))
            .find(({ validation }) => !validation.isValid);

        if (invalidEmail) {
            toast(invalidEmail.validation.reason, { type: "error" });
            return;
        }


        const hasDuplicateSelectedEmail = normalizedSelectedEmails.some(
            (email, index) => normalizedSelectedEmails.indexOf(email) !== index
        );

        if (hasDuplicateSelectedEmail) {
            toast("This e-mail has already been added.", { type: "error" });
            return;
        } else {
            const existingSelectedEmails = selectedEmails.map(normalizeEmailValue);
            dispatchAction(updateStateData({
                // Preserve the "New:" prefix because validateReportShareInfo strips it before submit.
                selected_brand_userforReportshare: action === "createOption" ? selectedOptions.map((value) => {
                    if (existingSelectedEmails.includes(normalizeEmailValue(value))) return value;
                    return "New: " + String(value || "").replace(/^New:\s*/i, "").trim();
                }) : selectedOptions,
                show_report_cancel_confirmation: true
            }))
        }
    };

    const inputLabel = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Enter Recipient(s)
            <HtmlTooltip title={
                <Box width={300}>
                    <Typography variant="body2">
                        Add an e-mail address by pressing the Return key or select one from your list of Favorites. You can add multiple e-mail addresses.
                    </Typography>
                    <Box height={10} />
                    <Typography variant="body2">
                        Brand accounts can be marked as favorites in the User Settings.
                    </Typography>
                </Box>
            }>
                <InfoOutlined
                    sx={{ color: "#AF3241", cursor: "pointer", fontSize: "16px" }}
                />
            </HtmlTooltip>
        </Box>
    );

    return typeof userStateDetails.brand_user_list_options !== "undefined" ? (
        <>
            {/* Custom replacement for MUI Autocomplete with the same share-email behavior. */}
            <EmailRecipientSelector
                value={selectedEmails}
                options={options}
                sharedEmails={sharedUserListitems}
                noOptionsText="No matches found"
                onChange={handleEmailChange}
                inputId="cutomTextForEmailSelection"
                inputName="shared_user_list"
                label={inputLabel}
                fieldError={emailListError}
            />
            <Box height={15} />
            {
                [...userStateDetails.brand_user_list_options]
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map(userOption => {
                        // Favorite chips are shortcuts; hide them once selected or already shared.
                        if (userOption.fav === true && !sharedUserListitems.includes(userOption.label) && selectedEmailLabels.map(normalizeEmailValue).includes(normalizeEmailValue(userOption.label)) === false) {
                            return (<Chip
                                key={userOption.value}
                                label={userOption.label}
                                size='small'
                                variant='outlined'
                                onClick={() => {
                                    const updatedSelectedUsers = typeof userStateDetails.selected_brand_userforReportshare !== "undefined"
                                        ? [...userStateDetails.selected_brand_userforReportshare, userOption.label] : [userOption.label];
                                    dispatchAction(updateStateData({
                                        selected_brand_userforReportshare: updatedSelectedUsers,
                                    }));
                                }}
                                sx={{
                                    borderColor: "#AF3241",
                                    color: "#AF3241",
                                    marginRight: 1,
                                    cursor: "pointer",
                                    marginBottom: 1,
                                    '& .MuiChip-deleteIcon': {
                                        color: "#AF3241",
                                    },
                                }}
                            />)
                        }
                        return <></>;
                    })
            }
        </>
    ) : (<></>);
}

export default EmailSelection;
